<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import EcommerceAPI from 'dashboard/api/ecommerce';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  listClass: {
    type: String,
    default: 'px-6 py-3 space-y-2',
  },
});

const { t } = useI18n();
const { formatDate: formatWithLocale, localeTag } = useDateLocale();

const orders = ref([]);
const loading = ref(true);
const error = ref('');
const expandedOrderId = ref(null);

const fetchOrders = async () => {
  try {
    loading.value = true;
    const response = await EcommerceAPI.getContactOrders(props.contactId);
    orders.value = response.data.orders;
  } catch (e) {
    error.value = e.response?.data?.error || 'Error loading orders';
  } finally {
    loading.value = false;
  }
};

watch(() => props.contactId, fetchOrders, { immediate: true });

const formatDate = dateString => formatWithLocale(dateString, 'd MMM yyyy');

const thumbUrl = url => {
  if (!url) return url;
  return url.includes('?') ? `${url}&width=80` : url;
};

const formatCurrency = (amount, currency) => {
  return new Intl.NumberFormat(localeTag.value, {
    style: 'currency',
    currency: currency || 'BRL',
  }).format(amount);
};

const toggleOrder = orderId => {
  expandedOrderId.value = expandedOrderId.value === orderId ? null : orderId;
};

const getFinancialStatusClass = status => {
  const classes = {
    paid: 'bg-n-teal-5 text-n-teal-12',
    partially_paid: 'bg-n-amber-5 text-n-amber-12',
    refunded: 'bg-n-ruby-5 text-n-ruby-12',
  };
  return classes[status] || 'bg-n-solid-3 text-n-slate-12';
};

const getFulfillmentStatusClass = status => {
  const classes = {
    delivered: 'text-n-teal-9',
    shipped: 'text-n-teal-9',
    partially_shipped: 'text-n-amber-9',
    unfulfilled: 'text-n-ruby-9',
    returned: 'text-n-ruby-9',
  };
  return classes[status] || 'text-n-slate-11';
};

const getFulfillmentLabel = status => {
  const key = `CONTACTS_LAYOUT.SIDEBAR.ECOMMERCE_ORDERS.FULFILLMENT_STATUS.${status}`;
  return t(key, status);
};

const getFinancialLabel = status => {
  const key = `CONTACTS_LAYOUT.SIDEBAR.ECOMMERCE_ORDERS.FINANCIAL_STATUS.${status}`;
  return t(key, status);
};
</script>

<template>
  <div
    v-if="loading"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div v-else-if="error" class="px-6 py-10 text-sm text-center text-n-ruby-11">
    {{ error }}
  </div>
  <p
    v-else-if="!orders.length"
    class="px-6 py-10 text-sm leading-6 text-center text-n-slate-11"
  >
    {{ t('CONTACTS_LAYOUT.SIDEBAR.ECOMMERCE_ORDERS.EMPTY_STATE') }}
  </p>
  <div v-else :class="props.listClass">
    <div
      v-for="order in orders"
      :key="order.id"
      class="rounded-lg border border-n-weak overflow-hidden"
    >
      <button
        class="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-n-alpha-1 transition-colors"
        @click="toggleOrder(order.id)"
      >
        <div class="flex flex-col gap-1 min-w-0">
          <div class="flex items-center gap-2">
            <a
              v-if="order.admin_url"
              :href="order.admin_url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm font-medium text-n-slate-12 hover:underline truncate"
              @click.stop
            >
              #{{ order.order_number }}
            </a>
            <span v-else class="text-sm font-medium text-n-slate-12 truncate">
              #{{ order.order_number }}
            </span>
            <span
              :class="getFinancialStatusClass(order.financial_status)"
              class="text-[10px] px-1.5 py-0.5 rounded shrink-0"
            >
              {{ getFinancialLabel(order.financial_status) }}
            </span>
          </div>
          <div class="text-xs text-n-slate-11 flex items-center gap-2">
            <span>{{ formatDate(order.placed_at) }}</span>
            <span class="text-n-slate-10">&middot;</span>
            <span class="font-medium text-n-slate-12">
              {{ formatCurrency(order.total_amount, order.currency) }}
            </span>
          </div>
        </div>
        <i
          :class="
            expandedOrderId === order.id
              ? 'i-lucide-chevron-up'
              : 'i-lucide-chevron-down'
          "
          class="text-n-slate-10 text-base shrink-0"
        />
      </button>

      <div
        v-if="expandedOrderId === order.id && order.order_items.length"
        class="border-t border-n-weak bg-n-alpha-black1"
      >
        <div
          v-for="item in order.order_items"
          :key="item.id"
          class="flex items-center gap-3 px-4 py-2.5 border-b border-n-weak last:border-b-0"
        >
          <img
            v-if="item.image_url"
            :src="thumbUrl(item.image_url)"
            :alt="item.title"
            class="w-10 h-10 rounded object-cover shrink-0"
          />
          <div
            v-else
            class="w-10 h-10 rounded bg-n-solid-3 flex items-center justify-center shrink-0"
          >
            <i class="i-lucide-image text-n-slate-10 text-sm" />
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm text-n-slate-12 truncate">{{ item.title }}</p>
            <p
              v-if="item.variant_title"
              class="text-xs text-n-slate-11 truncate"
            >
              {{ item.variant_title }}
            </p>
          </div>
          <div class="text-right shrink-0">
            <p class="text-sm font-medium text-n-slate-12">
              {{ formatCurrency(item.unit_price, order.currency) }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ t('CONTACTS_LAYOUT.SIDEBAR.ECOMMERCE_ORDERS.QUANTITY') }}:
              {{ item.quantity }}
            </p>
          </div>
        </div>
        <div
          v-if="order.fulfillment_status"
          class="px-4 py-2 flex items-center gap-1.5"
        >
          <span class="text-xs text-n-slate-11"
            >{{
              t(
                'CONTACTS_LAYOUT.SIDEBAR.ECOMMERCE_ORDERS.FULFILLMENT_STATUS.LABEL'
              )
            }}:</span
          >
          <span
            :class="getFulfillmentStatusClass(order.fulfillment_status)"
            class="text-xs font-medium"
          >
            {{ getFulfillmentLabel(order.fulfillment_status) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
